import {
  DocumentVersionStatus,
  signatureManifestEntrySchema,
  type AcknowledgeDocumentInput,
  type ApproveDocumentInput,
  type CreateDocumentInput,
  type CreateDocumentVersionInput,
  type DocumentListFilter,
  type ObsoleteDocumentInput,
  type SignableRecordType,
  type SignatureManifestEntry,
  type UpdateDistributionInput,
} from '@qualicloud/contracts';
import { z } from 'zod';
import { ApiScope, request, requestFile, toApiUrl } from './client';
import type {
  AdminDocumentListResponse,
  DashboardSummary,
  DocumentDetail,
  DocumentFullView,
  DocumentStatusCounts,
  DownloadTicket,
  ExportedPdfTicket,
  MasterListResponse,
  PendingTask,
  PrintAuthorization,
} from './types';

/** Referência ao upload já finalizado no MEDIA. O binário nunca passa pelo CORE. */
export interface UploadReference {
  readonly uploadId: string;
  readonly fileName: string;
}

const manifestListSchema = z.array(signatureManifestEntrySchema);

/** Filtros da tela de Administração — ged.md §14.3. */
export interface AdminDocumentFilter {
  readonly page: number;
  readonly pageSize: number;
  readonly search?: string | undefined;
  readonly documentTypeId?: string | undefined;
  readonly authorUserId?: string | undefined;
  readonly status?: readonly DocumentVersionStatus[] | undefined;
  /** `AAAA-MM-DD`. Período de criação do documento. */
  readonly createdFrom?: string | undefined;
  readonly createdTo?: string | undefined;
}

/**
 * Normaliza a resposta de `GET /documents/counts`.
 *
 * Aceita tanto `{ total, byStatus: {...} }` quanto o registro achatado
 * `{ APPROVED: n, ... }`, e trata chave ausente como zero. Motivo: contador é
 * informação SECUNDÁRIA — derrubar a Lista Mestra inteira porque um contador
 * veio num invólucro diferente seria trocar um defeito cosmético por uma tela
 * fora do ar. O dado que governa a listagem continua vindo de `GET /documents`.
 */
function normalizeCounts(payload: unknown): DocumentStatusCounts {
  const root = typeof payload === 'object' && payload !== null
    ? (payload as Record<string, unknown>)
    : {};
  const source =
    typeof root.byStatus === 'object' && root.byStatus !== null
      ? (root.byStatus as Record<string, unknown>)
      : root;

  const byStatus: Partial<Record<DocumentVersionStatus, number>> = {};
  let sum = 0;
  for (const status of Object.values(DocumentVersionStatus)) {
    const value = source[status];
    const count = typeof value === 'number' && Number.isFinite(value) ? value : 0;
    byStatus[status] = count;
    sum += count;
  }

  const declaredTotal = root.total;
  return {
    byStatus,
    total:
      typeof declaredTotal === 'number' && Number.isFinite(declaredTotal) ? declaredTotal : sum,
  };
}

export const documentsApi = {
  /** Lista Mestra — paginada, ordenável e com a ACL aplicada no servidor. */
  list(filter: Partial<DocumentListFilter>): Promise<MasterListResponse> {
    return request<MasterListResponse>({
      scope: ApiScope.CORE,
      path: '/documents',
      query: filter as Record<string, unknown>,
    });
  },

  detail(documentId: string): Promise<DocumentDetail> {
    return request<DocumentDetail>({
      scope: ApiScope.CORE,
      path: `/documents/${documentId}`,
    });
  },

  /**
   * Detalhe AGREGADO — uma viagem em vez de cinco.
   *
   * Sem esta rota a tela de documento faria detalhe → versões → manifesto de
   * cada versão → decisões → permissões efetivas, com a terceira multiplicada
   * pelo número de versões (o N+1 clássico). O manifesto de assinaturas vem
   * junto, o que também evita depender de `GET /signatures/...` — que é a rota
   * cujo caminho diverge entre o gateway e o CORE.
   */
  full(documentId: string): Promise<DocumentFullView> {
    return request<DocumentFullView>({
      scope: ApiScope.BFF,
      path: `/documents/${documentId}/full`,
    });
  },

  /**
   * O CORE devolve o `DocumentDetail` COMPLETO, não `{id}`.
   *
   * O tipo antigo funcionava por acidente — `document-form.page.tsx` testa
   * `'id' in result` e `DocumentDetail` tem `id`. Declarar menos do que o
   * servidor devolve faz o cliente descartar dado que já está na resposta e
   * pedir de novo; e o `{versionId}` de `createVersion`, que o servidor nunca
   * devolveu, seria `undefined` se alguém o usasse.
   */
  create(input: CreateDocumentInput & { upload: UploadReference }): Promise<DocumentDetail> {
    return request<DocumentDetail>({
      scope: ApiScope.CORE,
      path: '/documents',
      method: 'POST',
      body: input,
    });
  },

  /**
   * Nova versão. `upload` é opcional de propósito: é a AUSÊNCIA dele que
   * dispara a regra "alterar metadado exige novo arquivo" — e essa decisão é do
   * SERVIDOR, comparando com o estado persistido (ged.md §6.2, divergência
   * D-VER-03 corrigida em relação ao legado, que decidia no cliente).
   */
  createVersion(
    documentId: string,
    input: CreateDocumentVersionInput & { upload?: UploadReference | null },
  ): Promise<DocumentDetail> {
    return request<DocumentDetail>({
      scope: ApiScope.CORE,
      path: `/documents/${documentId}/versions`,
      method: 'POST',
      body: input,
    });
  },

  /** Altera SOMENTE ACL e distribuição. Não gera versão (ged.md §6.2). */
  updateDistribution(documentId: string, input: UpdateDistributionInput): Promise<DocumentDetail> {
    return request<DocumentDetail>({
      scope: ApiScope.CORE,
      path: `/documents/${documentId}/distribution`,
      method: 'PATCH',
      body: input,
    });
  },

  /**
   * Ato ASSINADO: aprova ou reprova uma versão.
   *
   * Os três atos assinados devolvem o `DocumentDetail` já atualizado. As telas
   * hoje ignoram o retorno e invalidam o cache, mas um tipo que mente é uma
   * armadilha para quem for usá-lo amanhã.
   */
  decide(
    documentId: string,
    versionId: string,
    input: ApproveDocumentInput,
  ): Promise<DocumentDetail> {
    return request<DocumentDetail>({
      scope: ApiScope.CORE,
      path: `/documents/${documentId}/versions/${versionId}/decision`,
      method: 'POST',
      body: input,
    });
  },

  /** Ato ASSINADO: obsoleta o documento. Não incrementa a versão. */
  obsolete(documentId: string, input: ObsoleteDocumentInput): Promise<DocumentDetail> {
    return request<DocumentDetail>({
      scope: ApiScope.CORE,
      path: `/documents/${documentId}/obsolete`,
      method: 'POST',
      body: input,
    });
  },

  /** Ato ASSINADO: registra ciência da versão distribuída (ISO 13485 §4.2.4). */
  acknowledge(
    documentId: string,
    input: AcknowledgeDocumentInput,
  ): Promise<DocumentDetail> {
    return request<DocumentDetail>({
      scope: ApiScope.CORE,
      path: `/documents/${documentId}/acknowledge`,
      method: 'POST',
      body: input,
    });
  },

  /** URL pré-assinada de vida curta. O servidor verifica a flag DOWNLOAD e audita. */
  async download(documentId: string, versionId?: string): Promise<DownloadTicket> {
    const ticket = await request<DownloadTicket>({
      scope: ApiScope.CORE,
      path: `/documents/${documentId}/download`,
      query: versionId ? { versionId } : undefined,
    });
    // O servidor devolve caminho relativo; quem sabe a base da API é o cliente.
    return { ...ticket, url: toApiUrl(ticket.url) };
  },

  /** PDF/A com manifesto de assinaturas e marca d'água quando exigida. */
  async exportPdf(documentId: string, versionId?: string): Promise<ExportedPdfTicket> {
    const ticket = await request<ExportedPdfTicket>({
      scope: ApiScope.CORE,
      path: `/documents/${documentId}/export/pdf`,
      query: versionId ? { versionId } : undefined,
    });
    // Mesmo caminho de entrega do download: `toApiUrl` não mexe em URL absoluta.
    return { ...ticket, url: toApiUrl(ticket.url) };
  },

  /** Exporta a Lista Mestra usando exatamente a consulta e a ACL da tela. */
  exportMasterList(
    filter: Partial<DocumentListFilter>,
    format: 'csv' | 'pdf',
  ): Promise<{ blob: Blob; fileName: string }> {
    return requestFile({
      scope: ApiScope.CORE,
      path: '/documents/master-list/export',
      query: { ...filter, format },
      fallbackFileName: `lista-mestra.${format}`,
    });
  },

  /**
   * Manifesto de assinaturas de um registro — §11.50(b).
   *
   * O caminho é o DECLARADO PELO GATEWAY (`GET /signatures/:recordType/:recordId`),
   * que é o único contrato que o navegador conhece. Para documento, o manifesto
   * já vem dentro de `full()`; este método existe para os demais tipos de
   * registro assinável (NC, CAPA), que não têm agregação própria.
   */
  manifest(recordType: SignableRecordType, recordId: string): Promise<SignatureManifestEntry[]> {
    return request<SignatureManifestEntry[]>({
      scope: ApiScope.CORE,
      path: `/signatures/${encodeURIComponent(recordType)}/${encodeURIComponent(recordId)}`,
      responseSchema: manifestListSchema,
    });
  },

  /**
   * Contadores do painel — `getDashboardSummary()` do legado (ged.md §14.5).
   *
   * `documentTypeId` reproduz o `$tipo_id` do legado, que alimentava o combo de
   * tipos do painel.
   */
  dashboardSummary(documentTypeId?: string): Promise<DashboardSummary> {
    return request<DashboardSummary>({
      scope: ApiScope.CORE,
      path: '/documents/dashboard/summary',
      query: documentTypeId ? { documentTypeId } : undefined,
    });
  },

  myTasks(): Promise<PendingTask[]> {
    return request<PendingTask[]>({
      scope: ApiScope.CORE,
      path: '/documents/my-tasks',
    });
  },

  /**
   * Contadores por situação — `countDoc()` do legado (ged.md §14.4, §15.10).
   *
   * Recebe os MESMOS filtros da Lista Mestra, exceto a situação: o contador de
   * cada situação precisa ignorar a situação selecionada, senão só a chip ativa
   * teria número diferente de zero. Corrige o BUG-29, em que os contadores
   * eram sempre globais e discordavam da lista exibida.
   */
  async counts(
    filter: Omit<Partial<DocumentListFilter>, 'status' | 'page' | 'pageSize'> = {},
  ): Promise<DocumentStatusCounts> {
    const payload = await request<unknown>({
      scope: ApiScope.CORE,
      path: '/documents/counts',
      query: filter as Record<string, unknown>,
    });
    return normalizeCounts(payload);
  },

  /**
   * Listagem ADMINISTRATIVA — `getAllDocumentsAdmin()` do legado
   * (ged.md §14.3, §16.10).
   *
   * Ignora a ACL do documento e exige `document:read:all`. O acesso é auditado
   * pelo servidor; a tela declara isso ao usuário antes de mostrar o resultado.
   */
  adminList(filter: AdminDocumentFilter): Promise<AdminDocumentListResponse> {
    return request<AdminDocumentListResponse>({
      scope: ApiScope.CORE,
      path: '/documents/admin',
      query: filter as unknown as Record<string, unknown>,
    });
  },

  /**
   * Autoriza a impressão — `printDocument()` do legado (ged.md §15.15).
   *
   * O servidor verifica a flag `DOWNLOAD` e grava o evento `PRINT` na trilha
   * ANTES de devolver o token. É por isso que a tela não pode chamar
   * `window.print()` direto: sem esta chamada não existe registro de quem
   * imprimiu o quê, que é a única regressão de trilha que o módulo tinha.
   */
  printToken(documentId: string, versionId?: string): Promise<PrintAuthorization> {
    return request<PrintAuthorization>({
      scope: ApiScope.CORE,
      path: `/documents/${documentId}/print-token`,
      method: 'POST',
      body: versionId ? { versionId } : {},
    });
  },
};
